import { Text } from 'blaze-ui/Text/Text'
import Styles from './WeeklyBudgetInput.module.scss'
import cx from 'classnames'
import CurrencyInput from 'react-currency-input-field'
import { useCallback, useMemo } from 'react'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

interface WeeklyBudgetInputProps {
  value?: number
  onChange?: (value: number) => void
}

export const WeeklyBudgetInput = ({ value, onChange }: WeeklyBudgetInputProps) => {
  const { data } = useExternalIntegrations()

  const currencyCode = useMemo<string | undefined>(() => {
    const externalIntegrations = data?.external_integrations ?? []
    const adIntegration = externalIntegrations.find(
      (integration: ExternalIntegration) => integration.provider === ExternalIntegrationEnum.MetaAds,
    )
    if (adIntegration?.provider === ExternalIntegrationEnum.MetaAds) {
      return adIntegration.additional_data?.currency ?? undefined
    }
    return undefined
  }, [data])

  const isDisabled = useMemo(() => !currencyCode, [currencyCode])

  const handleValueChange = useCallback(
    (value: string | undefined) => {
      if (!onChange || isDisabled) return

      const newValue = value ? parseInt(value, 10) : 0
      if (isFinite(newValue) && newValue >= 0) {
        onChange(newValue)
      }
    },
    [onChange, isDisabled],
  )

  const label = useMemo(() => (currencyCode ? `Weekly budget in ${currencyCode}` : 'Weekly budget'), [currencyCode])

  return (
    <div className={Styles.root}>
      <div className={Styles.labelContainer}>
        <Text variant="secondary" color="var(--dark-40)">
          {label}
        </Text>
      </div>
      <div className={cx(Styles.inputContainer, { [Styles.disabled]: isDisabled })}>
        <CurrencyInput
          disabled={isDisabled}
          allowNegativeValue={false}
          allowDecimals={false}
          className={cx(Styles.currencyInput, { [Styles.disabled]: isDisabled })}
          intlConfig={{ locale: 'en-US', currency: currencyCode ?? undefined }}
          defaultValue={value ?? 0}
          onValueChange={handleValueChange}
          aria-label={label}
        />
      </div>
    </div>
  )
}
